import type {
	GeneratedAssetManifest,
	GeneratedAssetManifestEntry,
	GeneratedAssetVariant,
} from "../../Domain/GeneratedAssetManifest.ts";

const generatedAssetManifestUrl = "/images/generated-assets.manifest.json";

let cachedGeneratedAssetManifest: GeneratedAssetManifest | null = null;
let loadingGeneratedAssetManifest: Promise<GeneratedAssetManifest | null> | null = null;

export interface GeneratedAssetResolutionOptions {
	cssWidth?: number;
	cssHeight?: number;
	dpr?: number;
	viewportWidth?: number;
	deviceClass?: "mobile" | "tablet" | "desktop";
	preferReducedData?: boolean;
}

const isGeneratedAssetManifestEntry = (value: unknown): value is GeneratedAssetManifestEntry =>
	typeof value === "object" &&
	value !== null &&
	"id" in value &&
	"hash" in value &&
	"size" in value &&
	"mimeType" in value &&
	"url" in value &&
	typeof (value as GeneratedAssetManifestEntry).id === "string" &&
	typeof (value as GeneratedAssetManifestEntry).hash === "string" &&
	typeof (value as GeneratedAssetManifestEntry).size === "number" &&
	typeof (value as GeneratedAssetManifestEntry).mimeType === "string" &&
	typeof (value as GeneratedAssetManifestEntry).url === "string";

const isGeneratedAssetVariant = (value: unknown): value is GeneratedAssetVariant =>
	typeof value === "object" &&
	value !== null &&
	"hash" in value &&
	"size" in value &&
	"mimeType" in value &&
	"url" in value &&
	typeof (value as GeneratedAssetVariant).hash === "string" &&
	typeof (value as GeneratedAssetVariant).size === "number" &&
	typeof (value as GeneratedAssetVariant).mimeType === "string" &&
	typeof (value as GeneratedAssetVariant).url === "string";

const isGeneratedAssetManifest = (value: unknown): value is GeneratedAssetManifest =>
	typeof value === "object" &&
	value !== null &&
	"version" in value &&
	(value as GeneratedAssetManifest).version === 1 &&
	"entries" in value &&
	typeof (value as GeneratedAssetManifest).entries === "object" &&
	(value as GeneratedAssetManifest).entries !== null;

export const primeGeneratedAssetManifest = (manifest: GeneratedAssetManifest | null): void => {
	cachedGeneratedAssetManifest = manifest;
};

export const getGeneratedAssetManifest = (): GeneratedAssetManifest | null =>
	cachedGeneratedAssetManifest;

export const getGeneratedAssetManifestEntry = (
	id: string,
): GeneratedAssetManifestEntry | undefined => {
	return cachedGeneratedAssetManifest?.entries[id];
};

const getConnectionSaveData = (): boolean => {
	const connection =
		typeof navigator === "undefined"
			? undefined
			: (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
	return connection?.saveData === true;
};

const getRuntimeDeviceClass = (): GeneratedAssetResolutionOptions["deviceClass"] => {
	const viewportWidth =
		typeof window === "undefined" ? undefined : Math.max(1, Math.floor(window.innerWidth));
	if (!viewportWidth) {
		return undefined;
	}
	if (viewportWidth <= 640) {
		return "mobile";
	}
	if (viewportWidth <= 1024) {
		return "tablet";
	}
	return "desktop";
};

const getTargetAssetWidth = (
	options: GeneratedAssetResolutionOptions,
	deviceClass: GeneratedAssetResolutionOptions["deviceClass"],
): number | undefined => {
	const dpr =
		typeof options.dpr === "number" && Number.isFinite(options.dpr)
			? Math.max(1, options.dpr)
			: typeof window === "undefined"
				? 1
				: Math.max(1, window.devicePixelRatio || 1);
	const cssWidth =
		typeof options.cssWidth === "number" && Number.isFinite(options.cssWidth)
			? options.cssWidth
			: typeof options.viewportWidth === "number" && Number.isFinite(options.viewportWidth)
				? options.viewportWidth
				: typeof window === "undefined"
					? undefined
					: window.innerWidth;
	if (typeof cssWidth !== "number") return undefined;
	const targetWidth = Math.max(1, Math.ceil(cssWidth * dpr));
	// A viewport is only a rough proxy for rendered card size. Keep mobile art compact.
	if (options.cssWidth === undefined && deviceClass === "mobile") {
		return Math.min(targetWidth, 640);
	}
	if (options.cssWidth === undefined && deviceClass === "tablet") {
		return Math.min(targetWidth, 1024);
	}
	return targetWidth;
};

const getVariantWidth = (
	variant: Pick<GeneratedAssetVariant, "width" | "height">,
): number | undefined => {
	if (typeof variant.width === "number" && Number.isFinite(variant.width)) {
		return variant.width;
	}
	if (typeof variant.height === "number" && Number.isFinite(variant.height)) {
		return variant.height;
	}
	return undefined;
};

const toVariantCandidate = (
	entry: GeneratedAssetManifestEntry,
): GeneratedAssetVariant & { original: true } => ({
	hash: entry.hash,
	size: entry.size,
	mimeType: entry.mimeType,
	url: entry.url,
	width: entry.width,
	height: entry.height,
	quality: entry.quality,
	deviceClass: entry.deviceClass,
	original: true,
});

const chooseGeneratedAssetVariant = (
	entry: GeneratedAssetManifestEntry,
	options: GeneratedAssetResolutionOptions,
): GeneratedAssetVariant => {
	const variants = (entry.variants ?? []).filter(isGeneratedAssetVariant);
	if (variants.length === 0) {
		return toVariantCandidate(entry);
	}
	const candidates = [...variants, toVariantCandidate(entry)];
	const deviceClass = options.deviceClass ?? getRuntimeDeviceClass();
	const reducedData = options.preferReducedData ?? getConnectionSaveData();
	if (reducedData) {
		return (
			[...candidates].sort((left, right) => left.size - right.size)[0] ?? toVariantCandidate(entry)
		);
	}
	const targetWidth = getTargetAssetWidth(options, deviceClass);
	const deviceCandidates = deviceClass
		? candidates.filter((candidate) => candidate.deviceClass === deviceClass)
		: [];
	const sizedCandidates = deviceCandidates.length > 0 ? deviceCandidates : candidates;
	if (!targetWidth) {
		return (
			[...sizedCandidates].sort((left, right) => left.size - right.size)[0] ??
			toVariantCandidate(entry)
		);
	}
	const sorted = [...candidates].sort((left, right) => {
		const leftWidth = getVariantWidth(left) ?? Number.POSITIVE_INFINITY;
		const rightWidth = getVariantWidth(right) ?? Number.POSITIVE_INFINITY;
		const leftFits = leftWidth >= targetWidth;
		const rightFits = rightWidth >= targetWidth;
		if (leftFits !== rightFits) {
			return leftFits ? -1 : 1;
		}
		if (leftFits) {
			return leftWidth - rightWidth || left.size - right.size;
		}
		return rightWidth - leftWidth || left.size - right.size;
	});
	return sorted[0] ?? entry;
};

export const resolveGeneratedAssetUrl = (
	id: string,
	fallbackUrl: string,
	options: GeneratedAssetResolutionOptions = {},
): string => {
	const entry = getGeneratedAssetManifestEntry(id);
	if (!entry) {
		return fallbackUrl;
	}
	return chooseGeneratedAssetVariant(entry, options).url;
};

export const loadGeneratedAssetManifest = async (
	fetchManifest: typeof fetch = fetch,
): Promise<GeneratedAssetManifest | null> => {
	if (cachedGeneratedAssetManifest) {
		return cachedGeneratedAssetManifest;
	}
	if (loadingGeneratedAssetManifest) {
		return loadingGeneratedAssetManifest;
	}

	loadingGeneratedAssetManifest = (async () => {
		try {
			const response = await fetchManifest(generatedAssetManifestUrl);
			if (!response.ok) {
				cachedGeneratedAssetManifest = null;
				return null;
			}
			const parsed = (await response.json()) as unknown;
			if (!isGeneratedAssetManifest(parsed)) {
				cachedGeneratedAssetManifest = null;
				return null;
			}
			const entries = Object.fromEntries(
				Object.entries(parsed.entries).flatMap(([id, entry]) => {
					if (!isGeneratedAssetManifestEntry(entry)) {
						return [];
					}
					const normalizedEntry = {
						...entry,
						variants: Array.isArray(entry.variants)
							? entry.variants.filter(isGeneratedAssetVariant)
							: undefined,
					};
					return [[id, normalizedEntry]];
				}),
			);
			cachedGeneratedAssetManifest = {
				version: 1,
				entries,
			};
			return cachedGeneratedAssetManifest;
		} catch {
			cachedGeneratedAssetManifest = null;
			return null;
		} finally {
			loadingGeneratedAssetManifest = null;
		}
	})();

	return loadingGeneratedAssetManifest;
};
